import { useEffect, useState } from "react";
import { default as axios } from "../axiosConfig";
import { useNavigate } from "react-router-dom";
import MetricGroupCard from "./MetricGroupCard";
import Skeleton from "@mui/material/Skeleton";
import GroupIcon from "@mui/icons-material/Group";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { API_GetExpiringJobContracts, API_GetHiringManagerUserSummary } from "../Assets/ApiUrlContainer";
import { ModalPrompt } from "./ModalPrompt";

function MetricCardSkeleton() {
  return (
    <div className="metric-card">
      <Skeleton variant="text" width={120} height={30} />
      <Skeleton variant="text" width={60} height={40} />
    </div>
  );
}

const HiringManagerLandingPage = ({ user }) => {
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [modalToggleState, setModalToggleState] = useState(false);
  const [expiringContracts, setExpiringContracts] = useState([]);
  const navigate = useNavigate();
  const isLoading = !summary && !error;

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await axios.get(`${API_GetHiringManagerUserSummary}?email=${user.Email}`);
        setSummary(res.data);
        setError(null);
      } catch (error) {
        console.error("Error fetching Hiring Manager summary:", error);
        setError(
          "Failed to load dashboard data. Please try refreshing the page."
        );
      }
    };

    if (user?.Email) {
      fetchSummary();
    }
  }, [user?.Email]);

  useEffect(() => {
    const fetchExpiringContracts = async () => {
      try {
        const response = await axios.get(`${API_GetExpiringJobContracts}/${user.Id}/${user.IsGoogleUser}`);
        setExpiringContracts(response.data || []);

      } catch (err) {
        console.log("Error fetching the expiring contracts: ", err);
      }
    }

    fetchExpiringContracts();
  }, [user])

  const openExpiringContractsModal = async () => {
    setModalToggleState(!modalToggleState);
  }

  return (
    <>
      <div className="landing-content">
        <div className="metric-panel">
          <div className="welcome-header">
            <h1>
              Welcome {user.FirstName} {user.LastName}
            </h1>
          </div>

          {error && <div className="error-message">{error}</div>}

          {isLoading ? (
            <div className="metric-group">
              <div className="landing-grid">
                {[...Array(6)].map((_, i) => (
                  <MetricCardSkeleton key={i} />
                ))}
              </div>
            </div>
          ) : (
            <>
              {/* Two-column grid layout like Admin */}
              <div className="grid-two-columns">
                {/* Left Column */}
                <div className="multGroupedCards">
                  <MetricGroupCard
                    className="group-card group-user"
                    title={
                      <>
                        <GroupIcon style={{ marginRight: "8px" }} />
                        User Summary
                      </>
                    }
                    metrics={[
                      {
                        label: "Active Consultants",
                        value: summary.totals?.activeConsultants ?? 0,
                      },
                    ]}
                  />

                  <MetricGroupCard
                    className="group-card group-contracts"
                    title={
                      <>
                        <CalendarMonthIcon style={{ marginRight: "8px" }} />
                        Contracts
                      </>
                    }
                    metrics={[
                      {
                        label: "Expiring Soon",
                        value: summary.contracts?.expiringSoon ?? 0,
                        onClick: () => openExpiringContractsModal()
                      },
                    ]}
                  />
                </div>

                {/* Right Column */}
                <div>
                  <MetricGroupCard
                    className="group-card group-jobs"
                    title={
                      <>
                        <WorkOutlineIcon style={{ marginRight: "8px" }} />
                        Job Activity
                      </>
                    }
                    metrics={[
                      {
                        label: "New Jobs This Week",
                        value: summary.jobs?.newJobsThisWeek ?? 0,
                        onClick: () => navigate("/jobs"),
                      },
                      {
                        label: "Applications This Week",
                        value: summary.jobs?.applicationsThisWeek ?? 0,
                      },
                    ]}
                  />
                </div>
              </div>

              {/* Pending Approvals Full Width */}
              <MetricGroupCard
                className="group-card group-approvals"
                title={
                  <>
                    <AssignmentTurnedInIcon style={{ marginRight: "8px" }} />
                    Pending Approvals
                  </>
                }
                metrics={[
                  {
                    label: "Leave Requests",
                    value: summary.approvals?.leaveRequests ?? 0,
                    onClick: () => navigate("/tasks/leaverequests"),
                  },
                  {
                    label: "Timesheets to Approve",
                    value: summary.approvals?.timesheetsToApprove ?? 0,
                    onClick: () => navigate("/tasks?tab=timesheets"),
                  },
                ]}
              />
            </>
          )}
        </div>
      </div>

      <ModalPrompt
        onModalClose={() => setModalToggleState(!modalToggleState)}
        customModalContainerStyles={expiringContracts.length > 0 && { width: '50% !important' }}
        modalToggleState={modalToggleState}
        modalTitle={"Expiring Job Contacts"}
        modalContent={
          <>
            <div className="expiringContractsModal">
              {expiringContracts.length === 0 && (
                <div className="noContracts">
                  <span>There are no contracts expiring within 30 days</span>
                </div>
              )}

              {expiringContracts.length > 0 && (
                <>
                  <div className="contractEntryHeading">
                    <div className="jobName">Job</div>
                    <div className="candidateName">Candidate</div>
                    <div className="expiration">Expiration Date</div>
                  </div>

                  <div className="contractEntryDataContainer">
                    {expiringContracts.map(contract => (
                      <div className="contractEntryData" key={contract.ContractId}>
                        <div className="jobName"><div className="spanText">{contract.JobName}</div></div>
                        <div className="candidateName"><div className="spanText">{contract.CandidateName}</div></div>
                        <div className="expiration"><div className="spanText">{contract.ContractExpireDate.split("T")[0]}</div></div>
                      </div>
                    ))}
                  </div>
                </>
              )}

            </div>
          </>
        }
      />
    </>
  );
};

export default HiringManagerLandingPage;
